<script setup>
import {computed, ref, watch} from "vue";
import startupsService from "@/services/startups.service";
import {titleCase} from "../../../../frontend/src/helpers/string";
import PrimaryButton from "@/components/ui/PrimaryButton.vue";
import Editor from "../../../../frontend/src/components/Editor.vue";

const emit = defineEmits(['closed', 'saved']);
const showModal = ref(false);
const id = ref(null);
const form = ref({});
const saving = ref(false);

let objectFields = {};

watch(showModal, (value) => {
    if (!value) {
        emit('closed');
    }
});

const reportData = computed(() => {
    let report = {};

    for (let field in form.value) {
        if (Array.isArray(form.value[field])) {
            let items = [];

            for (let item of form.value[field]) {
                if (typeof item === 'object') {
                    let itemFields = {};

                    for (let subField in item) {
                        if (item[subField]) {
                            itemFields[subField] = item[subField];
                        }
                    }

                    if (Object.keys(itemFields).length) {
                        items.push(itemFields);
                    }
                } else if (item) {
                    items.push(item);
                }
            }

            if (items.length) {
                report[field] = items;
            }
        } else if (typeof form.value[field] === 'object') {
            let sectionFields = {};

            for (let subField in form.value[field]) {
                if (form.value[field][subField]) {
                    sectionFields[subField] = form.value[field][subField];
                }
            }

            if (Object.keys(sectionFields).length) {
                report[field] = sectionFields;
            }
        } else if (form.value[field]) {
            report[field] = form.value[field];
        }
    }

    return report;
})

async function getData() {
    form.value = {};
    let structure = {};

    const {data: [prompt, report]} = await startupsService.getAiReportForEdit(id.value);

    for (let field in prompt) {
        if (Array.isArray(prompt[field])) {
            structure[field] = [];

            if (typeof prompt[field][0] === 'object') {
                objectFields[field] = Object.keys(prompt[field][0]);
            }

            if (field in report && Array.isArray(report[field])) {
                for (let item of report[field]) {
                    if (field in objectFields) {
                        let itemFields = {};

                        for (let subField of objectFields[field]) {
                            itemFields[subField] = '';

                            if (subField in item) {
                                itemFields[subField] = item[subField];
                            }
                        }

                        structure[field].push(itemFields);

                    } else if (typeof item !== 'object') {
                        structure[field].push(item);
                    }
                }
            }
        } else if (typeof prompt[field] === 'object') {
            structure[field] = {};

            for (let subField in prompt[field]) {
                structure[field][subField] = '';

                if (field in report && subField in report[field]) {
                    structure[field][subField] = report[field][subField];
                }
            }
        } else {
            if (field in report) {
                structure[field] = report[field];
            } else {
                structure[field] = '';
            }
        }
    }

    if ('add_block' in report) {
        structure['add_block'] = report['add_block'];
    } else {
        structure['add_block'] = '';
    }

    form.value = structure;
}

function addItem(field) {
    if (field in objectFields) {
        let itemFields = {};

        for (let subField of objectFields[field]) {
            itemFields[subField] = '';
        }

        form.value[field].push(itemFields);
    } else {
        form.value[field].push('');
    }
}

async function save() {
    await startupsService.saveAiReport(id.value, reportData.value);

    emit('saved');
    showModal.value = false;
}

function open(startupId) {
    id.value = startupId;
    showModal.value = true;
    objectFields = [];

    getData();
}

defineExpose({
    open
});
</script>

<template>
    <b-modal v-model="showModal" title="Edit AI report" modal-class="modal-w-xl">
        <div v-for="field in Object.keys(form)" :key="field">
            <div class="title">{{titleCase(field)}}</div>

            <div v-if="field === 'add_block'">
                <Editor v-model="form.add_block"/>
            </div>

            <div v-else-if="Array.isArray(form[field])">
                <div v-for="(_, index) in form[field]" :key="index" class="flex-line">

                    <div v-if="typeof form[field][index] === 'object'" class="w-100">
                        <div v-for="subField in Object.keys(form[field][index])" :key="subField">
                            <div class="line">
                                {{titleCase(subField)}}

                                <input v-model="form[field][index][subField]" class="form-control form-control--mid">
                            </div>
                        </div>
                    </div>

                    <input v-else v-model="form[field][index]" class="w-100 form-control form-control--mid ">

                    <a class="link ml-2" @click="form[field].splice(index, 1)">Remove</a>
                </div>

                <a class="link" @click="addItem(field)">+Add</a>
            </div>

            <div v-else-if="typeof form[field] === 'object'">
                <div v-for="subField in Object.keys(form[field])" :key="subField">
                    <div class="line">
                        {{titleCase(subField)}}

                        <input v-model="form[field][subField]" class="form-control form-control--mid">
                    </div>
                </div>
            </div>

            <div v-else>
                <input v-model="form[field]" class="form-control form-control--mid w-100">
            </div>
        </div>

        <template #modal-footer>
            <PrimaryButton
                :disabled="saving"
                @click="save"
            />
        </template>
    </b-modal>
</template>

<style scoped>
    .title {
        font-size: 1.1rem;
        font-weight: bold;
        margin-top: 20px;
        margin-bottom: 5px;
    }

    .line {
        display: flex;
        align-items: center;
        margin-bottom: 5px;

        input {
            flex: 1;
            margin-left: 1rem;
        }
    }

    input {
        margin-bottom: 5px;
    }
</style>
